import type {
  TopArtistsSortSubpaths,
  TopCollectorsSortSubpaths,
  TopCuratorsSortSubpaths,
  TopReleasesSortSubpaths,
} from '@pages-shared/topCharts';

export const RELEASE_COLUMNS = () =>
  [
    {
      Header: 'Rank',
      accessor: 'rank',
    },
    {
      Header: '',
      accessor: 'releaseInfo',
    },
    {
      Header: 'Unique Collectors',
      accessor: 'uniqueCollectors',
    },
    {
      Header: 'Mints',
      accessor: 'nftsSold',
    },
    {
      Header: 'Total Volume',
      accessor: 'totalSales',
    },
  ] as const;

export const ARTIST_COLUMNS = () =>
  [
    {
      Header: 'Rank',
      accessor: 'rank',
    },
    {
      Header: '',
      accessor: 'artistInfo',
    },
    {
      Header: 'Unique Collectors',
      accessor: 'uniqueCollectors',
    },
    {
      Header: 'Mints',
      accessor: 'nftsSold',
    },
    {
      Header: 'Total Volume',
      accessor: 'totalSales',
    },
  ] as const;

export const COLLECTOR_COLUMNS = (fullText: boolean) =>
  [
    {
      Header: 'Rank',
      accessor: 'rank',
    },
    {
      Header: '',
      accessor: 'userInfo',
    },
    {
      Header: fullText ? 'Artists Supported' : 'Supported',
      accessor: 'creatorsSupported',
    },
    {
      Header: 'Mints',
      accessor: 'nftsBought',
    },
    {
      Header: fullText ? 'Total Spent' : 'Spent',
      accessor: 'totalSpent',
    },
  ] as const;

export const CURATOR_COLUMNS = () =>
  [
    {
      Header: 'Rank',
      accessor: 'rank',
    },
    {
      Header: '',
      accessor: 'userInfo',
    },
    {
      Header: 'Sounds Referred',
      accessor: 'soundsReferred',
    },
    {
      Header: 'Mints Driven',
      accessor: 'mintsQuantity',
    },
    {
      Header: 'Total Volume',
      accessor: 'totalVolume',
    },
  ] as const;

export const TRENDING_RELEASE_ACCESSOR_TO_SORT = {
  nftsSold: 'mints',
  primarySales: 'total-volume',
  secondarySales: 'total-volume',
  totalSales: 'total-volume',
  uniqueCollectors: 'unique-collectors',
} as const satisfies Record<string, TopReleasesSortSubpaths>;

export const TRENDING_ARTIST_ACCESSOR_TO_SORT = {
  nftsSold: 'mints',
  primarySales: 'total-volume',
  secondarySales: 'total-volume',
  totalSales: 'total-volume',
  uniqueCollectors: 'unique-collectors',
} as const satisfies Record<string, TopArtistsSortSubpaths>;

export const TRENDING_SORT_TO_DISPLAY = (fullText: boolean) => {
  return {
    mints: 'Mints',
    'total-volume': 'Total Volume',
    'unique-collectors': 'Unique Collectors',
    'artists-supported': fullText ? 'Artists Supported' : 'Supported',
    'total-spent': fullText ? 'Total Spent' : 'Spent',
    'sounds-referred': 'Sounds Referred',
  } as const satisfies Record<
    | TopReleasesSortSubpaths
    | TopArtistsSortSubpaths
    | TopCollectorsSortSubpaths
    | TopCuratorsSortSubpaths,
    string
  >;
};

export const TRENDING_COLLECTOR_ACCESSOR_TO_SORT = {
  nftsBought: 'mints',
  creatorsSupported: 'artists-supported',
  totalSpent: 'total-spent',
} as const satisfies Record<string, TopCollectorsSortSubpaths>;

export const TRENDING_CURATOR_ACCESSOR_TO_SORT = {
  mintsQuantity: 'mints',
  soundsReferred: 'sounds-referred',
  totalVolume: 'total-volume',
} as const satisfies Record<string, TopCuratorsSortSubpaths>;

const TRENDING_RELEASE_ACCESSOR = Object.keys(TRENDING_RELEASE_ACCESSOR_TO_SORT)[0];

export type TrendingReleaseAccessorType =
  (typeof TRENDING_RELEASE_ACCESSOR)[keyof typeof TRENDING_RELEASE_ACCESSOR];

const TRENDING_ARTIST_ACCESSOR = Object.keys(TRENDING_ARTIST_ACCESSOR_TO_SORT)[0];

export type TrendingArtistAccessorType =
  (typeof TRENDING_ARTIST_ACCESSOR)[keyof typeof TRENDING_ARTIST_ACCESSOR];

const TRENDING_COLLECTOR_ACCESSOR = Object.keys(TRENDING_COLLECTOR_ACCESSOR_TO_SORT)[0];

export type TrendingCollectorAccessorType =
  (typeof TRENDING_COLLECTOR_ACCESSOR)[keyof typeof TRENDING_COLLECTOR_ACCESSOR];
